import type {
  DiscoveryPlacement,
  IntegrationSurface,
} from "../../api/integrations";

type DiscoverySession = {
  id: string;
  owner?: DiscoveryPlacement;
  dismissed?: boolean;
};
const memory = new Map<string, DiscoverySession>();
const key = (userId: string) => `integration-discovery:${userId}`;
export const DISCOVERY_IMPRESSION_CAP = 3;
const IMPRESSION_WINDOW_MS = 7 * 86_400_000;

export function discoverySession(userId: string): DiscoverySession {
  if (memory.has(userId)) return memory.get(userId)!;
  let session: DiscoverySession | undefined;
  try {
    const stored = JSON.parse(sessionStorage.getItem(key(userId)) ?? "null");
    if (stored && typeof stored.id === "string") session = stored;
  } catch {
    /* Storage can be disabled; retain the session in memory. */
  }
  session ??= { id: crypto.randomUUID() };
  saveDiscoverySession(userId, session);
  return session;
}
export function saveDiscoverySession(
  userId: string,
  session: DiscoverySession
) {
  memory.set(userId, session);
  try {
    sessionStorage.setItem(key(userId), JSON.stringify(session));
  } catch {
    /* In-memory session remains valid. */
  }
}
export function reserveDiscovery(
  userId: string,
  placement: DiscoveryPlacement
): DiscoverySession | null {
  const session = discoverySession(userId);
  if (session.dismissed || (session.owner && session.owner !== placement))
    return null;
  const reserved = { ...session, owner: placement };
  saveDiscoverySession(userId, reserved);
  return reserved;
}
export function releaseDiscovery(
  userId: string,
  placement: DiscoveryPlacement
) {
  const session = discoverySession(userId);
  if (session.owner === placement && !session.dismissed)
    saveDiscoverySession(userId, { id: session.id });
}
export function dismissDiscoverySession(userId: string) {
  saveDiscoverySession(userId, {
    ...discoverySession(userId),
    dismissed: true,
  });
}

const impressionsKey = (userId: string, placement: DiscoveryPlacement) =>
  `${key(userId)}:${placement}:impressions`;
function readImpressions(
  userId: string,
  placement: DiscoveryPlacement,
  now: number
): number[] {
  const stored = JSON.parse(
    localStorage.getItem(impressionsKey(userId, placement)) ?? "[]"
  );
  return Array.isArray(stored)
    ? stored.filter(
        (at): at is number =>
          typeof at === "number" &&
          now - at < IMPRESSION_WINDOW_MS &&
          at - now <= 60_000
      )
    : [];
}
export function recentImpressions(
  userId: string,
  placement: DiscoveryPlacement,
  now = Date.now()
): number {
  try {
    return readImpressions(userId, placement, now).length;
  } catch {
    return 0;
  }
}
export function recordImpression(
  userId: string,
  placement: DiscoveryPlacement,
  now = Date.now()
) {
  let prior: number[] = [];
  try {
    prior = readImpressions(userId, placement, now);
  } catch {
    /* A malformed stored value is overwritten below. */
  }
  try {
    localStorage.setItem(
      impressionsKey(userId, placement),
      JSON.stringify([...prior, now])
    );
  } catch {
    /* Storage can be disabled; the impression is simply not counted. */
  }
}

export interface DiscoveryAttribution {
  placement: DiscoveryPlacement;
  surface?: IntegrationSurface;
  baseline: string[];
  startedAt: number;
}
const attributionKey = (userId: string) => `${key(userId)}:source`;
export function setDiscoveryAttribution(
  userId: string,
  value: DiscoveryAttribution
) {
  try {
    sessionStorage.setItem(attributionKey(userId), JSON.stringify(value));
  } catch {
    /* Analytics is best effort. */
  }
}
export function getDiscoveryAttribution(
  userId: string
): DiscoveryAttribution | null {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(attributionKey(userId)) ?? "null"
    );
    return value && Date.now() - value.startedAt < 3_600_000 ? value : null;
  } catch {
    return null;
  }
}
export function clearDiscoveryAttribution(userId: string) {
  try {
    sessionStorage.removeItem(attributionKey(userId));
  } catch {
    /* Analytics is best effort. */
  }
}
